/**
 * Courbes d'accélération — logique pure (§4 : aucune dépendance).
 *
 * Les trois courbes du §2 du document de direction artistique, et rien d'autre :
 * une bibliothèque d'easings pléthorique invite à choisir au hasard, ce qui produit
 * une interface dont le mouvement n'a plus de grammaire.
 *
 * Ces fonctions sont la première brique du moteur d'animation de l'étape 4a. Elles
 * prennent une progression normalisée et rendent une progression transformée ; elles
 * ne connaissent ni le temps, ni les frames, ni le DOM.
 */

/** Ramène une progression dans [0, 1]. */
export function clamp01(t: number): number {
  if (Number.isNaN(t)) {
    return 0;
  }
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/** Interpolation linéaire. `t` n'est volontairement pas borné : `easeOutBack` dépasse. */
export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/** Par défaut : démarre vite, s'arrête en douceur. */
export function easeOutCubic(t: number): number {
  const p = clamp01(t);
  return 1 - (1 - p) ** 3;
}

/**
 * Pour tout ce qui apparaît : dépasse légèrement la cible avant de revenir.
 * C'est ce petit dépassement qui donne l'impression de matière ; sans lui, un
 * panneau qui s'ouvre paraît glisser sur du verre.
 */
export function easeOutBack(t: number): number {
  const overshoot = 1.70158;
  const p = clamp01(t) - 1;
  return 1 + (overshoot + 1) * p ** 3 + overshoot * p ** 2;
}

/**
 * Chute avec accélération (E§1.1) : lente au départ, rapide à l'arrivée.
 *
 * C'est la seule courbe qui ne vienne pas de D§2. Elle n'est pas destinée à l'interface
 * — un panneau ne « tombe » pas — mais au **monde** : un bâtiment qui descend à vitesse
 * constante paraît descendu à la grue, pas lâché.
 */
export function easeInCubic(t: number): number {
  return clamp01(t) ** 3;
}

/** Pour la caméra : accélère et décélère symétriquement, sans à-coup. */
export function easeInOutCubic(t: number): number {
  const p = clamp01(t);
  return p < 0.5 ? 4 * p ** 3 : 1 - (-2 * p + 2) ** 3 / 2;
}

export type EasingFunction = (t: number) => number;

export const EASING_FUNCTIONS = {
  out: easeOutCubic,
  back: easeOutBack,
  inOut: easeInOutCubic,
  in: easeInCubic,
} as const satisfies Record<string, EasingFunction>;

export type EasingName = keyof typeof EASING_FUNCTIONS;
